import { flatMap, groupBy, memoize, uniq, without, takeRightWhile } from 'lodash-es';
import { customElement, html } from '@adobe/edex/ui/edex-lit';
import {
  EdexTagName,
  SubjectI18nLabel,
  Locale,
  ADOBE_FOR_EDU_JP,
  ADOBE_FOR_EDU,
} from '@adobe/edex/ui/shared/constants';
import { I18nKey } from '@adobe/edex/ui/shared/constants/i18n';
import { $t, getTranslation, getCurrentLocale } from '@adobe/edex/ui/shared/config/i18n';
import { SearchEntityType, SearchKey, SearchType } from '@adobe/edex/ui/shared/types/search';
import {
  TreeItem,
  TreeSelectorParams,
  TreeSelectorPredicate,
} from '@adobe/edex/ui/components/shared/tree-selector/tree-selector';
import '@adobe/edex/ui/components/shared/tree-selector/tree-selector';
import '@adobe/edex/ui/components/shared/tree-selector/item-selector';
import {
  AcademicLevelSchema,
  DurationSchema,
  ProductCountSchema,
  ProductSchema,
  SubjectSchema,
  TimingSchema,
} from '@adobe/edex/ui/types';
import { TreeSelectorChangePayload } from '@adobe/edex/ui/types/payload';
import { SearchPageBase } from '@adobe/edex/ui/components/search-page/search-page.base';
import { classifyProducts, sortAcademicLevels, sortSubjects } from '@adobe/edex/ui/shared/utils/metadata';
import './search-filters.scss';
import { getAssetURL } from '@adobe/edex/ui/shared/utils';
import { equalsSearchParentType } from '@adobe/edex/ui/components/search-page/search.utils';

type IdKey = 'id' | 'i18nLabel' | 'urlLabel';

export enum AllID {
  type = '__all__',
  subject = '__all__',
  product = '__all__',
  timing = '__all__',
  duration = '__all__',
  author = '__all__',
  academicLevel = '5f93600c-19c4-4799-a603-fe3913681237', // academic level is the only metadata table that has an "all" item, hence the real id from DB
}

const getChildrenIDs = memoize((item: TreeItem, idKey: IdKey) => item?.children?.map((c) => c[idKey]) || []);

const productCountUsed2treeItems = memoize((products: ProductCountSchema[]): TreeItem[] => {
  return products.map((product) => {
    return {
      id: product.id,
      i18nLabel: product.name?.replace('Adobe', '').trim(),
    };
  });
});

const products2treeItems = memoize((products: ProductSchema[]): TreeItem[] => {
  const classified = classifyProducts(products);
  const items = [...classified.entries()]
    .map(([classification, products]) => {
      const parent = {
        id: classification,
        i18nLabel: classification,
        children: products.map((product) => ({
          ...product,
          id: `${classification}-${product.id}`, // child id must be unique
          i18nLabel: product.title?.replace('Adobe', '').trim(), // Avoid Adobe in label.
          parentID: classification,
        })),
      };
      return parent.children.length ? parent : null;
    })
    .filter(Boolean);
  return [
    {
      id: AllID.product,
      i18nLabel: I18nKey.searchFiltersProductsAll,
    },
    ...items,
  ];
});

const subjects2treeItems = memoize((subjects: SubjectSchema[]): TreeItem[] => {
  subjects = sortSubjects(subjects);
  return [
    {
      id: AllID.subject,
      i18nLabel: SubjectI18nLabel.all,
    },
    ...subjects,
  ];
});

const academicLevels2treeItems = memoize((academicLevels: AcademicLevelSchema[]): TreeItem[] => {
  const byCategory = groupBy(academicLevels, ({ i18nCategory }) => i18nCategory);
  const academicLevelsArray = flatMap(
    Object.entries(byCategory).map(([i18nCategory, items]) => {
      const children = items.map((item) => ({
        ...item,
        parentID: i18nCategory,
      }));
      return i18nCategory
        ? [
            {
              id: i18nCategory,
              i18nLabel: i18nCategory,
              sortOrder: children[0]?.sortOrder,
              children,
            },
          ]
        : children;
    })
  );
  sortAcademicLevels(academicLevelsArray);
  return academicLevelsArray;
});

const timing2treeItems = memoize((timing: TimingSchema[]): TreeItem[] => {
  return [
    {
      id: AllID.timing,
      i18nLabel: I18nKey.searchFiltersActiveStudentTimeAll,
    },
    ...timing,
  ];
});

const duration2treeItems = memoize((durations: DurationSchema[]): TreeItem[] => {
  return [
    {
      id: AllID.duration,
      i18nLabel: I18nKey.searchFiltersDurationAll,
    },
    ...durations,
  ];
});

const getCourseTree = (locale) => {
  if (locale === Locale.de) {
    return [];
  }
  return [
    {
      id: SearchType.professionalLearning,
      i18nLabel: I18nKey.navProfessionalLearning,
      children: [
        {
          id: SearchType.selfPacedCourses,
          i18nLabel: I18nKey.selfPacedLabel,
          parentID: SearchType.professionalLearning,
        },
        {
          id: SearchType.toolkits,
          i18nLabel: I18nKey.toolkitLabel,
          parentID: SearchType.professionalLearning,
        },
      ],
    },
  ];
};

const getCollectionTree = (locale) => {
  if (locale === Locale.de || locale === Locale.jp) {
    return [];
  }
  return [
    {
      id: SearchType.collections,
      i18nLabel: I18nKey.navCollections,
    },
  ];
};

function getTypeTreeItems(): TreeItem[] {
  const locale = getCurrentLocale();
  return [
    {
      id: AllID.type,
      i18nLabel: I18nKey.searchFiltersTypeAll,
    },
    {
      id: SearchType.teachingResources,
      i18nLabel: I18nKey.navTeachingResources,
      children: [
        {
          id: SearchType.projects,
          i18nLabel: 'i18n.resourceType.Project_example',
          parentID: SearchType.teachingResources,
        },
        {
          id: SearchType.supplemental,
          i18nLabel: 'i18n.resourceType.Supplemental',
          parentID: SearchType.teachingResources,
        },
      ],
    },
    ...getCourseTree(locale),
    ...getCollectionTree(locale),
  ];
}

type TreeFilterSearchKey =
  | SearchKey.type
  | SearchKey.product
  | SearchKey.subject
  | SearchKey.ageLevel
  | SearchKey.time
  | SearchKey.duration
  | SearchKey.template
  | SearchKey.author;

interface TreeFilterParams {
  idKey: IdKey;
  searchKey: TreeFilterSearchKey;
  name: string;
  items: TreeItem[];
  allID: AllID;
  virtualParent: boolean;
  isProduct?: boolean;
  beforeUpdate?: (value: any) => any;
}

@customElement(EdexTagName.searchFilters)
export class SearchFilters extends SearchPageBase {
  get useLightDom() {
    return true;
  }

  siteSection = null;
  seo = null;

  expandedIds = new Set<SearchKey>(Object.values(SearchKey));

  get subjects(): TreeItem[] {
    return subjects2treeItems(this.metadata.subjects);
  }

  get products(): TreeItem[] {
    return productCountUsed2treeItems(this.metadata.productUsedCount);
  }

  get academicLevels(): TreeItem[] {
    return academicLevels2treeItems(this.metadata.academicLevels);
  }

  get timing(): TreeItem[] {
    return timing2treeItems(this.metadata.timing);
  }

  get durations(): TreeItem[] {
    return duration2treeItems(this.metadata.duration);
  }

  get authors(): TreeItem[] {
    const ADOBE_AUTHOR = getCurrentLocale() === Locale.jp ? ADOBE_FOR_EDU_JP : ADOBE_FOR_EDU;
    return [
      {
        id: AllID.author,
        i18nLabel: I18nKey.allAuthor,
      },
      ADOBE_AUTHOR,
    ];
  }

  static get properties() {
    return {
      ...super.properties,
      resultsByType: { attribute: false },
    };
  }

  getDefaultItems = (): TreeItem[] => {
    return [
      {
        id: '02d9b931-fdba-4386-bb87-6f82b6c35948',
        i18nLabel: 'Acrobat',
      },
      {
        id: '9cb258a2-5b8c-4e6b-a197-c8f5cec7c938',
        i18nLabel: 'Adobe Express',
      },
      {
        id: 'c714eff0-aa52-437c-aed7-da1af1f986b0',
        i18nLabel: 'Illustrator',
      },
      {
        id: 'd66d3ee2-7dc4-419c-a1df-34f8a5b2f6a4',
        i18nLabel: 'InDesign',
      },
      {
        id: '31f8e1e7-a879-4eae-8149-0d4c980c2778',
        i18nLabel: 'Photoshop',
      },
      {
        id: '17a836aa-7c20-47e6-8b67-dd52b232a571',
        i18nLabel: 'Premiere Pro',
      },
      {
        id: 'e0efd80f-9602-44d4-b3da-5c0361a7595e',
        i18nLabel: 'Premiere Rush',
      },
    ];
  };

  getProductItems = (items: TreeItem[]) => {
    // [Acrobat, Express, Illustrator, InDesign, Photoshop, Premiere Pro, Premiere Rush]
    const defaultProducts = new Set<string>([
      '02d9b931-fdba-4386-bb87-6f82b6c35948',
      '9cb258a2-5b8c-4e6b-a197-c8f5cec7c938',
      'c714eff0-aa52-437c-aed7-da1af1f986b0',
      'd66d3ee2-7dc4-419c-a1df-34f8a5b2f6a4',
      '31f8e1e7-a879-4eae-8149-0d4c980c2778',
      '17a836aa-7c20-47e6-8b67-dd52b232a571',
      'e0efd80f-9602-44d4-b3da-5c0361a7595e',
    ]);
    const products = items.filter((item) => !defaultProducts.has(item.id));
    products.sort((a, b) => a.i18nLabel.localeCompare(b.i18nLabel));
    return [...this.getDefaultItems(), ...products];
  };

  handleClear() {
    this.navigate({
      path: '/search',
      queryParams: { [SearchKey.query]: this.query }, // preserve `query` only
      mergeQueryParams: false,
    });
    this.resetResultsScrollPosition();
  }

  handleTreeItemChange = ({ idKey, searchKey, allID, virtualParent, beforeUpdate }: TreeFilterParams) => ({
    detail,
  }: CustomEvent<TreeSelectorChangePayload>) => {
    if (!detail) {
      return;
    }
    const { checked, item, parent, items, isProduct, isTemplateFilter } = detail;
    let {
      searchParams: { [searchKey]: selected },
    } = this;
    const isAll = item?.id === allID;
    let next: string[];
    if (isAll) {
      next = [];
    } else if (isProduct) {
      next = Array.from(items);
      searchKey = SearchKey.product;
    } else if (isTemplateFilter) {
      next = items;
      searchKey = SearchKey.template;
    } else {
      const children = getChildrenIDs(item, idKey);

      // virtual parent mode -> select all children if any
      if (virtualParent) {
        const ids = item.parentID || !item.children?.length ? [item[idKey]] : children;
        if (checked) {
          next = [...selected, ...ids];
        } else {
          next = without(selected, ...ids);
        }
      }
      // non virtual parent mode -> parent item itself can be selected
      else {
        const id = item[idKey];
        const parentID = parent?.[idKey];
        const siblings = getChildrenIDs(parent, idKey);
        const parentIsSelected = selected.includes(parentID);

        // always exclude parent and children if any
        selected = without(selected, parentID, ...children);

        if (checked) {
          next = [...selected, id];

          // all children are selected -> select parent
          if (parent && siblings.every((id) => next.includes(id))) {
            next = [parentID, ...without(next, ...siblings)];
          }
        } else {
          selected = parentIsSelected ? [...selected, ...siblings] : selected;
          next = without(selected, id);
        }
      }
    }
    next = beforeUpdate ? beforeUpdate(next) : next;
    this.updateSearch({
      [searchKey]: uniq(next),
    });
  };

  isTreeItemIndeterminate = ({ idKey, searchKey, items }: TreeFilterParams): TreeSelectorPredicate => (item) => {
    const {
      searchParams: { [searchKey]: selected },
    } = this;
    // must be parent
    if (item.parentID) {
      return false;
    }
    return (
      // at least one child is selected
      getChildrenIDs(item, idKey).some(
        (childID) =>
          // direct parent
          selected.includes(childID) ||
          // non-direct parent
          selected.some((id) =>
            getChildrenIDs(
              items.find((item) => item[idKey] === id),
              idKey
            ).includes(childID)
          )
      )
    );
  };

  isTreeItemChecked = ({ idKey, searchKey, allID, items }: TreeFilterParams): TreeSelectorPredicate => (
    item,
    parent
  ) => {
    const {
      searchParams: { [searchKey]: selected },
    } = this;
    const { children } = item;
    if (!selected.length) {
      return item.id === allID;
    }
    return (
      // item is selected
      selected.includes(item[idKey]) ||
      // all children are selected === parent is selected
      (children?.length && children.every((c) => selected.includes(c[idKey]))) ||
      // direct parent is selected
      selected.includes(parent?.[idKey]) ||
      // non-direct parent is selected
      selected.some((id) =>
        getChildrenIDs(
          items.find((item) => item[idKey] === id),
          idKey
        ).includes(item[idKey])
      )
    );
  };

  isTreeItemReadonly = ({ searchKey, allID }: TreeFilterParams): TreeSelectorPredicate => (item) => {
    const {
      searchParams: { [searchKey]: selected },
    } = this;
    return item.id === allID && !selected.length;
  };

  isTreeItemDisabled = ({ searchKey }: TreeFilterParams): TreeSelectorPredicate => (item, parent) => {
    if (searchKey === SearchKey.type) {
      if (item.id === AllID.type) {
        return false;
      }
      return !this.resultsByType[item.id];
    }
    return false;
  };

  handleFilterToggle = (event: Event) => {
    const searchKey = (event.target as Element).closest('sp-action-button').dataset.key as SearchKey;

    if (this.isTreeItemExpanded(searchKey)) {
      this.expandedIds.delete(searchKey);
    } else {
      this.expandedIds.add(searchKey);
    }
    this.requestUpdate();
  };

  isTreeItemExpanded = (searchKey: SearchKey) => this.expandedIds.has(searchKey);

  renderMobileHeader() {
    return html`
      <h2>Filters</h2>
      <div class="filter-close-icon" @click=${() => this.emitEvent('close')}><sp-icon-close></sp-icon-close></div>
    `;
  }

  renderTreeFilter(treeFilterParams: TreeFilterParams) {
    const { searchKey, name, items, isProduct } = treeFilterParams;
    const {
      searchParams: { [searchKey]: selected, template },
    } = this;
    if (!items) {
      return null;
    }
    const treeSelectorParams: TreeSelectorParams = {
      items,
      selected,
      template,
      isChecked: this.isTreeItemChecked(treeFilterParams),
      isIndeterminate: this.isTreeItemIndeterminate(treeFilterParams),
      isReadonly: this.isTreeItemReadonly(treeFilterParams),
      isDisabled: this.isTreeItemDisabled(treeFilterParams),
      inline: true,
    };
    return html`
      <div class="tree-filter" aria-expanded=${String(this.isTreeItemExpanded(searchKey))}>
        <div class="tree-filter-header">
          <sp-action-button quiet size="s" data-key="${searchKey}" @click=${this.handleFilterToggle}>
            <img src=${getAssetURL('caret.svg')} width="32" height="32" alt="" />
          </sp-action-button>
          <h4>${name}</h4>
        </div>
        ${this.isTreeItemExpanded(searchKey) && !isProduct
          ? html`
              <edex-tree-selector
                @change="${this.handleTreeItemChange(treeFilterParams)}"
                .params=${treeSelectorParams}
              ></edex-tree-selector>
            `
          : ''}
        ${this.isTreeItemExpanded(searchKey) && isProduct
          ? this.renderProductSearch(treeFilterParams, treeSelectorParams, selected)
          : ''}
      </div>
      <hr />
    `;
  }

  renderProductSearch(treeFilterParams, treeSelectorParams, selected) {
    return html`
      <edex-item-selector
        @change=${this.handleTreeItemChange(treeFilterParams)}
        .params=${treeSelectorParams}
        .defaultItems=${this.getDefaultItems()}
        .deletables=${selected}
        .showEditableTemplate=${this.isResource()}
      ></edex-item-selector>
    `;
  }

  renderTypeFilter() {
    return this.renderTreeFilter({
      idKey: 'id',
      searchKey: SearchKey.type,
      name: getTranslation(I18nKey.searchFiltersTypeHeading),
      allID: AllID.type,
      items: getTypeTreeItems(),
      virtualParent: false,
      // only one parent type can be selected
      beforeUpdate: (value: SearchType[]) =>
        takeRightWhile(value, (item, index) => !value[index + 1] || equalsSearchParentType(item, value[index + 1])),
    });
  }

  renderSubjectFilter() {
    const { subjects: items } = this;
    if (!this.showContent(SearchEntityType.resources) && !this.showContent(SearchEntityType.collections)) {
      return;
    }
    return this.renderTreeFilter({
      idKey: 'urlLabel',
      searchKey: SearchKey.subject,
      name: getTranslation(I18nKey.searchFiltersSubjectsHeading),
      allID: AllID.subject,
      items,
      virtualParent: false,
    });
  }

  renderProductFilter() {
    const { products: items } = this;
    const products = this.getProductItems(items);
    return this.renderTreeFilter({
      idKey: 'i18nLabel',
      searchKey: SearchKey.product,
      name: getTranslation(I18nKey.searchFiltersProductsHeading),
      allID: AllID.product,
      items: products,
      virtualParent: true,
      isProduct: true,
    });
  }

  renderAgeLevelFilter() {
    const { academicLevels: items } = this;
    if (!this.showContent(SearchEntityType.resources) && !this.showContent(SearchEntityType.collections)) {
      return;
    }
    return this.renderTreeFilter({
      idKey: 'urlLabel',
      searchKey: SearchKey.ageLevel,
      name: getTranslation(I18nKey.ages),
      allID: AllID.academicLevel,
      items,
      virtualParent: true,
    });
  }

  renderDurationFilter() {
    const { durations } = this;
    if (!this.showContent(SearchEntityType.courses)) {
      return;
    }
    return this.renderTreeFilter({
      idKey: 'urlLabel',
      searchKey: SearchKey.duration,
      name: getTranslation(I18nKey.searchFiltersCourseDurationHeading),
      allID: AllID.duration,
      items: durations,
      virtualParent: true,
    });
  }

  renderActiveStudentTimeFilter() {
    const { timing: items } = this;
    if (!this.showContent(SearchEntityType.resources)) {
      return;
    }
    return this.renderTreeFilter({
      idKey: 'urlLabel',
      searchKey: SearchKey.time,
      name: getTranslation(I18nKey.searchFiltersActiveStudentTimeHeading),
      allID: AllID.timing,
      items,
      virtualParent: true,
    });
  }

  renderAuthorFilter() {
    const { authors } = this;
    if (!this.showContent(SearchEntityType.resources)) {
      return;
    }

    return this.renderTreeFilter({
      idKey: 'id',
      searchKey: SearchKey.author,
      name: getTranslation(I18nKey.author),
      allID: AllID.author,
      items: authors,
      virtualParent: true,
    });
  }
  render() {
    if (!this.isMetadataLoaded()) {
      return null;
    }
    return html`
      <aside id="search-filters">
        <header>
          <div class="header mobile-only">${this.renderMobileHeader()}</div>
          <sp-divider class="mobile-only"></sp-divider>
          <div class="header">
            <h4>${$t(I18nKey.searchFiltersHeading)}</h4>
            <sp-link quiet @click="${this.handleClear}">${$t(I18nKey.searchFiltersClear)}</sp-link>
          </div>
        </header>
        <hr class="desktop-only" />
        <section>${this.renderTypeFilter()}</section>
        <section>${this.renderAgeLevelFilter()}</section>
        <section>${this.renderDurationFilter()}</section>
        <section>${this.renderSubjectFilter()}</section>
        <section>${this.renderProductFilter()}</section>
        <section>${this.renderActiveStudentTimeFilter()}</section>
        <section>${this.renderAuthorFilter()}</section>
      </aside>
    `;
  }
}
